import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { ITagDefinition } from '../../../../view/settings/api-automation-settings/tag-definitions/tag-definitions.model';
import { TagDefinitionService } from './tag-definition.service';
import {
  ActionTypes,
  TagDefinitionsLoading,
  TagDefinitionsLoaded,
  FetchError,
  GetTagDefinition,
  GetTagDefinitionCompleted,
  EditTagDefinition,
  EditTagDefinitionCompleted,
  AddTagDefinition,
  AddTagDefinitionCompleted,
  DeleteTagDefinitions,
  DeleteTagDefinitionsCompleted,
  SiteDataLoading,
  SiteDataCompleted,
  LineDataLoading,
  LineDataCompleted,
  StationsDataLoading,
  StationsDataCompleted,
  DestinationsDataLoading,
  DestinationsDataCompleted,
  HeartbeatSettingsDataLoading,
  HeartbeatSettingsDataCompleted,
  CleanerTagDefinitionsDataLoading,
  CleanerTagDefinitionsDataCompleted,
  DestinationMapDataLoading,
  DestinationMapDataCompleted,
} from './tag-definition.actions';
import { ApiAutomationLookupService } from '../shared/api-automation-lookup.service';
import { EApiAutomationResource } from '../shared/api-automation.model';
import { LineService } from '../../../../shared/service/line/line.service';
import { LineStationService } from '../../../../shared/service/line-station/line-station.service';
import { SiteService } from '../../../../shared/service/filter/site.service';

@Injectable()
export class TagDefinitionEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: TagDefinitionService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly siteService: SiteService,
    private readonly lineService: LineService,
    private readonly stationService: LineStationService,
    private readonly lookupService: ApiAutomationLookupService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.TAG_DEFINITIONS_DATA_LOADING),
      switchMap((payload: TagDefinitionsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (payload.params.siteIds?.length) {
          andConditions.push({ siteId: { $in: payload.params.siteIds } });
        }

        if (payload.params.lineIds?.length) {
          andConditions.push({ lineId: { $in: payload.params.lineIds } });
        }

        if (searchText) {
          andConditions.push({
            $or: [
              { tagName: { $cont: searchText } },
              { description: { $cont: searchText } },
              { valueOperator: { $cont: searchText } },
              { tagType: { $cont: searchText } },
            ],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new TagDefinitionsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_TAG_DEFINITION),
      switchMap((objectData: GetTagDefinition) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<ITagDefinition>) =>
            of(new GetTagDefinitionCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_TAG_DEFINITION),
      switchMap((objectData: AddTagDefinition) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ITagDefinition>) =>
            of(
              new AddTagDefinitionCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_TAG_DEFINITION),
      switchMap((objectData: EditTagDefinition) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ITagDefinition>) =>
            of(
              new EditTagDefinitionCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_TAG_DEFINITIONS),
      switchMap((objectData: DeleteTagDefinitions) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteTagDefinitionsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getSiteData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.SITES_DATA_LOADING),
      switchMap(() =>
        from(this.siteService.getData(new HttpParams().set('limit', '1000'))).pipe(
          switchMap((response) => of(new SiteDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getLineData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.LINES_DATA_LOADING),
      switchMap((objectData: LineDataLoading) =>
        this.lineService
          .getLines(
            new HttpParams()
              .set('s', JSON.stringify({ statusId: 1, siteId: objectData.siteId }))
              .set('limit', '1000')
              .set('join', 'lineTypeName'),
            true,
          )
          .pipe(
            switchMap((response) => of(new LineDataCompleted(response))),
            catchError((error) => of(new FetchError(error))),
          ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getStationsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.STATIONS_DATA_LOADING),
      switchMap((objectData: StationsDataLoading) =>
        from(this.stationService.getLineStationData(objectData.lineId)).pipe(
          switchMap((response) => of(new StationsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getDestinationsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DESTINATIONS_DATA_LOADING),
      switchMap((objectData: DestinationsDataLoading) =>
        this.lookupService.getDestinationOptions(objectData.tagType).pipe(
          switchMap((response) => of(new DestinationsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getHeartbeatSettingsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.HEARTBEAT_SETTINGS_DATA_LOADING),
      switchMap(() =>
        this.lookupService.getOptions(EApiAutomationResource.heartbeatSettings).pipe(
          switchMap((response) => of(new HeartbeatSettingsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getCleanerTagDefinitionsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.CLEANER_TAG_DEFINITIONS_DATA_LOADING),
      switchMap(() =>
        this.lookupService.getOptions(EApiAutomationResource.tagDefinition).pipe(
          switchMap((response) => of(new CleanerTagDefinitionsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getDestinationMapData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DESTINATION_MAP_DATA_LOADING),
      switchMap((objectData: DestinationMapDataLoading) =>
        this.lookupService.getDestinationOptionsMap(objectData.tagTypes).pipe(
          switchMap((response) => of(new DestinationMapDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
